import {useCallback, useEffect, useState} from 'react';
// redux
import {useAppDispatch, useAppSelector} from '../../../../app/hooks';
import {
  clearRoles,
  createAppUserRole,
  deleteAppUserRole,
  getAllAppUsers,
  getAllRoles,
  getAppUserById,
  getCurrentRolesForAppUser
} from '../../../../features/state/StateAppUser-slice';
import {useNotificationHelper} from '../../../../utilities/helpers/NotificationHelper';
import {GenerateAppUserColumnDefinition} from '../../../../components/stateful/table/TableHelper';
import {AppUser} from '../../../../utilities/interfaces/AppUser';
import {AppUserRole} from '../../../../utilities/interfaces/AppUserRole';
import {getAllTitles,} from '../../../../features/state/StateTitle-slice';

import {claimAPI} from "../../../../utilities/api/claimApi";
import {toNumber} from "lodash";


export const useAppUserManagementController = () => {
  // redux
  const appUsersValues: AppUser[] = useAppSelector((state) => state.appUser.appUsers);
  const fetchingAppUsers = useAppSelector((state) => state.appUser.fetchingAppUsers)
  const titlesList = useAppSelector((state) => state.titles.value)

  // state
  const dispatch = useAppDispatch();
  const [columnData, setColumnData] = useState<any>([]);
  const [openRolesModal, setOpenRolesModal] = useState<boolean>(false);
  const [openTitlesModal, setOpenTitlesModal] = useState<boolean>(false);
  const [selectedAppUser, setSelectedAppUser] = useState<AppUser>();
  const [currentTitles, setCurrentTitles] = useState<any>([]);
  const [availableTitles, setAvailableTitles] = useState<any>([]);

  const currentRoles = useAppSelector((state) => state.appUser.appUserCurrentRoles);
  const availableRoles = useAppSelector((state) => state.appUser.appUserAvailableRoles);
  const stateAvailableTitles = useAppSelector((state) => state.titles.value);



  const { addNotification } = useNotificationHelper();


  useEffect(() => {
    if (appUsersValues.length < 1) {
      dispatch(getAllAppUsers());
      dispatch(getAllRoles());
    }
    if (appUsersValues.length > 0 && columnData.length < 1) {
      generateColumnData();
    }
  });

  useEffect(() => {
    if(titlesList.length < 1){
      dispatch(getAllTitles());
    }
  });


  const generateColumnData = async () => {
    if (appUsersValues.length > 0) {
      let columns = await GenerateAppUserColumnDefinition(appUsersValues[0]);
      setColumnData(columns);
    }
  };


  const getAllUsers = () => {
    dispatch(getAllAppUsers());
    dispatch(getAllRoles());
    generateColumnData();
  };


  const openAppUserTitlesModal =  async (event: any, appUser: AppUser) => {
    setSelectedAppUser(appUser);

    await getUserTitleData(appUser);

    toggleTitlesModal();
  };

  const getUserTitleData = async (appUser: AppUser) => {

    let userClaims: any[] = await claimAPI.getClaimsByUserId(appUser.id);

    let userTitles = createUserTitlesFromUserClaims(stateAvailableTitles, userClaims)

    setCurrentTitles(userTitles);

    setAvailableTitles(filterAvailableTitlesBasedOnUserTitles(stateAvailableTitles, userTitles))
  }


  const createUserTitlesFromUserClaims = (availableTitles: any, currentTitles: any) => {

    let userTitles = [];

    for(let i = 0; i < availableTitles.length; i++){
      for( let j = 0; j < currentTitles.length; j++){
        if(availableTitles[i].titleNumber === toNumber(currentTitles[j].claimValue)){

          let userTitle = {
            claimUserId: currentTitles[j].id,
            appUserId: currentTitles[j].appUserId,
            ...availableTitles[i]
          }

          userTitles.push(userTitle)
        }
      }
    }

    return userTitles;
  };

  const filterAvailableTitlesBasedOnUserTitles = (availableTitles: any, currentTitles: any) => {
    let currentTitleNumber = currentTitles.map((x: any) => x.titleNumber);

    return availableTitles.filter((item: any )=> !currentTitleNumber.includes(item.titleNumber));
  };

  const openAppUserRolesModal = (event: any, appUser: AppUser) => {
    setSelectedAppUser(appUser);
    dispatch(clearRoles());
    dispatch(getCurrentRolesForAppUser(appUser.id));
    toggleRolesModal();
  };

  const toggleRolesModal = () => {
    openRolesModal ? setOpenRolesModal(false) : setOpenRolesModal(true);
  };

  const toggleTitlesModal = () => {
    openTitlesModal ? setOpenTitlesModal(false) : setOpenTitlesModal(true);
  };

  const getAppUserDetails = async (appUser: AppUser) => {
    dispatch(getAppUserById(appUser.id));
  };


  const addAppUserRole = useCallback(
      (roleName: any) => {
        let appUserRole: AppUserRole = {
          appUserId: selectedAppUser!.id,
          roleName: roleName.name,
        };
        dispatch(createAppUserRole(appUserRole));
      },
      [dispatch, selectedAppUser],
  );


  const removeAppUserRole = useCallback(
      (roleName: any) => {
        let appUserRole: AppUserRole = {
          appUserId: selectedAppUser!.id,
          roleName: roleName.name,
        };
        dispatch(deleteAppUserRole(appUserRole));
      },
      [dispatch, selectedAppUser],);



  const addUserTitle = 
    // eslint-disable-next-line react-hooks/rules-of-hooks
    useCallback(
        async (title: any) => {
          let appUserTitle = {
            appUserId: selectedAppUser!.id,
            claimTypeId: 1,
            claimTypeName: "Title",
            claimValue: title.titleNumber.toString(),
          };

          await claimAPI.addUserClaim(appUserTitle).then( () => {
            getUserTitleData(selectedAppUser!);
          });
        },
        [getUserTitleData, selectedAppUser],);


  const removeUserTitle =
      useCallback(
        async (title: any) => {

          await claimAPI.removeUserClaimById(title.claimUserId).then( () => {
            getUserTitleData(selectedAppUser!);
          });
        },
        [getUserTitleData, selectedAppUser],);

  const makeTitleName = (titlesArray: any) => {
    return titlesArray.map((title: any) => ({
      ...title,
      name : title.titleNumber + ".  " +title.titleName
    }));
  };

  return {
    // state
    columnData,
    selectedAppUser,
    appUsersValues,
    fetchingAppUsers,
    openRolesModal,
    openTitlesModal,
    currentRoles,
    availableRoles,
    currentTitles,
    availableTitles,
    // redux
    // functions
    openAppUserRolesModal,
    toggleRolesModal,
    openAppUserTitlesModal,
    toggleTitlesModal,
    getAppUserDetails,
    getAllUsers,
    addUserTitle,
    removeUserTitle,
    makeTitleName,
    // updateUserStatus,
    addAppUserRole,
    removeAppUserRole,
  };
};
